import React, { useState, useMemo, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe, faPlus, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import EditableTitle from "./EditableTitle";
import FilterOptionsPicker from "./FilterOptionsPicker";
import { useEscapeKey } from "../hooks/useEscapeKey";
import "../styles/GlobalFilters.css";

const GlobalFilters = ({
  globalFilters,
  setGlobalFilters,
  updateGlobalFilter,
  updateGlobalFilterTitle,
  toggleGlobalFilterMultiple,
  variables,
  tabs = [],
  removeGlobalFilter,
}) => {
  const [showList, setShowList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEscapeKey(showList, () => setShowList(false));

  useEffect(() => {
    if (!showList) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showList]);

  const handleSelectVariable = (key) => {
    const variable = variables[key];
    if (!variable) return;

    const label = variable.title || variable.Qtitle || key;
    const variableName = variable.varName?.[0]?.Name || variable.variableName || key;

    // --- BUILD OPTIONS EXACTLY LIKE YOUR JSON STRUCTURE ---
    const options =
      variable.values?.map((v, i) => ({
        Id: i + 1,
        Name: v.title || v.label || v.Name || v,
        ParentId: v.ParentId || [0],
        Selected: v.Selected ?? false,
        SortOrder: i + 1,
        Value: v.value || v.Value || v.title || v.Name || String(i + 1),
        Visible: v.Visible ?? false   // IMPORTANT: your expected JSON had Visible: false
      })) || [];


    // Find next numeric ID
    const nextId =
      globalFilters.length > 0
        ? Math.max(...globalFilters.map(f => Number(f.id))) + 1
        : 1;
    // --- FINAL GLOBAL FILTER OBJECT MATCHING YOUR JSON ---
    const newFilter = {
      id: nextId,
      global: 0,
      parent_id: null,
      label: label,
      variable_name: variableName,
      sort_order: globalFilters.length + 1,
      Selected: null,
      multiple: 1,
      visible: true,
      hide_on_tab: false,
      positioning: 0,
      RoleId: null,
      options: options,
      selectedValue: [],

      // 🔥 FULL ORIGINAL VARIABLE JSON AS-IT-IS
      varNames: {
        key: key,
        ...(variables[key] ?? {})   // Spread complete variable object
      }
    };


    setGlobalFilters((prev) => [...prev, newFilter]);
    setShowList(false);
    setSearchTerm("");
  };


  // Collect all variable names used in Local Filters across tabs
  const localFilterVariables = tabs.flatMap((tab) =>
    (tab.filters || []).map((f) => f.variable_name)
  );

  // Exclude both global + local variables
  const availableVariables = useMemo(
    () =>
      Object.keys(variables || {}).filter(
        (key) =>
          variables[key] != null &&
          !globalFilters.some((gf) => gf.variable_name === key) &&
          !localFilterVariables.includes(key) &&
          (variables[key].title || key).toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [variables, globalFilters, localFilterVariables, searchTerm]
  );

  return (
    <div className="gf-panel">
      {/* Header */}
      <div className="gf-header">
        <div className="gf-header-title">
          <FontAwesomeIcon icon={faGlobe} />
          <h4>Global Filters</h4>
          {globalFilters.length > 0 && (
            <span className="gf-count-badge">{globalFilters.length}</span>
          )}
        </div>

        <div className="gf-add-wrap" ref={dropdownRef}>
          <button className="gf-add-btn" onClick={() => setShowList((prev) => !prev)}>
            <FontAwesomeIcon icon={faPlus} /> Add Global Filter
          </button>

          {/* Dropdown List */}
          {showList && (
            <div className="gf-dropdown">
              <div className="gf-dropdown-search">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  type="text"
                  placeholder="Search variable..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="gf-dropdown-list">
                {availableVariables.length > 0 ? (
                  availableVariables.map((key) => (
                    <div
                      key={key}
                      className="gf-dropdown-item"
                      onClick={() => handleSelectVariable(key)}
                    >
                      {variables[key]?.title || key}
                    </div>
                  ))
                ) : (
                  <div className="gf-dropdown-empty">
                    All variables already added as filters.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Render Global Filters */}
      {globalFilters.length === 0 ? (
        <p className="gf-empty">No global filters added yet. Click "Add Global Filter" to create one.</p>
      ) : (
        <div className="gf-grid">
          {globalFilters.map((f) => {
            const isMulti = f.multiple === 1;

            return (
              <div key={f.id} className="gf-card">
                {/* Label + Remove Button */}
                <div className="gf-card-header">
                  <EditableTitle
                    value={f.dashboardTitle || f.label}
                    onSave={(title) => updateGlobalFilterTitle && updateGlobalFilterTitle(f.id, title)}
                    className="gf-card-title"
                    inputStyle={{
                      fontSize: "13.5px",
                      fontWeight: 600,
                      color: "var(--color-text-heading)",
                      border: "1px solid var(--color-border-gray)",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      width: "100%",
                    }}
                  />
                  <button
                    className="gf-remove-btn"
                    onClick={() => removeGlobalFilter(f.id)}
                    title="Remove Global Filter"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                {/* Multi-select Checkbox */}
                <label className="gf-multi-toggle">
                  <input
                    type="checkbox"
                    checked={isMulti}
                    onChange={() =>
                      toggleGlobalFilterMultiple && toggleGlobalFilterMultiple(f.id)
                    }
                  />
                  Multi-select
                </label>

                {/* Options — opens a searchable popup instead of a native select */}
                <FilterOptionsPicker
                  options={f.options || []}
                  isMulti={isMulti}
                  selectedValue={f.selectedValue}
                  onChange={(value) => updateGlobalFilter(f.id, value)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GlobalFilters;
